import { useTranslation } from 'react-i18next';
import { Bot } from 'lucide-react';
import type { LlmChatMessageUiAction, InquiryVersioningChoice } from '@paladin-monorepo/common';
import { LlmChatUiActionType } from '@paladin-monorepo/common';
import { MarkdownContent } from './MarkdownContent';
import { LlmChatInquiryVersioningActions } from './LlmChatInquiryVersioningActions';
import { LlmChatAgentQuestionActions } from './LlmChatAgentQuestionActions';
import { LlmChatToolSequence } from './LlmChatToolCallsSummary';
import { HIDDEN_LLM_CHAT_TOOL_NAMES } from './llm-chat-tool-display';
import type { StreamingToolEvent } from '@/hooks/useLlmChatStream';

interface StreamingIndicatorProps {
  content: string;
  uiAction?: LlmChatMessageUiAction | null;
  tools?: StreamingToolEvent[];
  onUiActionSend?: (answer: string) => void;
  onVersioningSelect?: (choice: InquiryVersioningChoice) => void;
}

export function StreamingIndicator({
  content,
  uiAction,
  tools,
  onUiActionSend,
  onVersioningSelect,
}: StreamingIndicatorProps) {
  const { t } = useTranslation();
  const visibleTools = (tools ?? []).filter((tool) => !HIDDEN_LLM_CHAT_TOOL_NAMES.has(tool.name));
  const hasRunningTool = visibleTools.some((tool) => tool.status === 'running');
  const sendUiActionAnswer = onUiActionSend ?? (onVersioningSelect ? (answer) => onVersioningSelect(answer as InquiryVersioningChoice) : undefined);

  return (
    <div className="flex gap-3">
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted">
        <Bot className="h-4 w-4" />
      </div>

      <div className="max-w-[75%] rounded-2xl bg-muted px-4 py-2.5">
        {visibleTools.length > 0 && (
          <div className={content ? 'mb-2' : undefined}>
            <LlmChatToolSequence
              items={visibleTools.map((tool, i) => ({
                key: `${tool.name}-${i}`,
                label: t(`llmChat.toolCalls.names.${tool.name}`, { defaultValue: tool.name }),
                status: tool.status,
              }))}
            />
          </div>
        )}

        {content ? (
          <div>
            <MarkdownContent content={content} />
            <span className="ml-0.5 inline-block h-4 w-1.5 animate-pulse bg-foreground/60" />
          </div>
        ) : !hasRunningTool ? (
          <div className="flex items-center gap-1 py-1">
            <span className="h-2 w-2 animate-bounce rounded-full bg-foreground/40 [animation-delay:0ms]" />
            <span className="h-2 w-2 animate-bounce rounded-full bg-foreground/40 [animation-delay:150ms]" />
            <span className="h-2 w-2 animate-bounce rounded-full bg-foreground/40 [animation-delay:300ms]" />
          </div>
        ) : null}

        {uiAction?.type === LlmChatUiActionType.InquiryVersioningChoice && sendUiActionAnswer && (
          <LlmChatInquiryVersioningActions
            uiAction={uiAction}
            onSelect={(choice) => sendUiActionAnswer(choice)}
          />
        )}

        {uiAction?.type === LlmChatUiActionType.AgentQuestion && sendUiActionAnswer && (
          <LlmChatAgentQuestionActions
            uiAction={uiAction}
            onSelect={sendUiActionAnswer}
          />
        )}
      </div>
    </div>
  );
}
